import { GetServerSidePropsContext } from "next";
import { Asset } from "contentful";
import { camelCase } from "lodash";
import {
  ChildInfo,
  Invoice,
  OrderSummary,
  getCheckoutSession,
  getOrderDetails,
  getOrderSummary,
  getShippingWindow,
  getSubscriptionPlans,
  updateChildInfo,
} from "lib/api/checkout";
import { isAPIError } from "lib/api/base";
import {
  Subscription,
  SubscriptionPlanCamelCase,
  getSubscription,
} from "lib/api/subscription";
import {
  PostCompleteGuidedQuizReturnType,
  getGuidedSignUpAnswers,
  postCompleteGuidedQuiz,
} from "lib/api/onboarding";
import {
  getReadingLevelNameFromEntryName,
  mapImageEntryReferencesByName,
} from "utils/readingLevelImageUtils";
import { getReadingLevelImage } from "components/Activation/activationUtil";
import { OnboardingQuizAnswerSchema } from "@literati/kids-api";
import {
  BudgetKey,
  BudgetValuesPartial,
  GenderKey,
  GenderValuesPartial,
  GoalsKey,
  GoalsValuesPartial,
  NormalizedOrderSummary,
  ReadingLevelKey,
  ReadingLevelValuesPartial,
  RelationshipKey,
  RelationshipValuesPartial,
  ThemePreferenceKey,
  ThemePreferenceValuesPartial,
} from "components/Step/stepTypes";
import { Step as ContentfulStep, ReadingSkillJsonItem } from "types/contentful";
import { getEntriesBySlug } from "utils/contentful/api";
import { getBookShelfQuizRecommendations } from "lib/api/bookShelfQuiz";
import {
  getPlanBoxImageProps,
  getSelectedPlan,
} from "components/PlanSelection/planSelectionUtil";
import { shuffleThemes } from "utils/GuidedSignUp";
import { getAuth } from "lib/api/account";
import {
  Plans,
  PlanPrepaid,
} from "components/PlanSelection/planSelectionTypes";
import {
  MEMBERSHIP_GOAL_OPTIONS,
  MEMBERSHIP_GOAL_PRESET_VALUES,
} from "components/Membership/membershipGoalsConfig";
import {
  MembershipSlug,
  MembershipState,
} from "components/Membership/membershipTypes";
import { SUBSCRIPTION_PLAN_SLUGS } from "components/PlanSelection/planSelectionTypes";

export const normalizeOrderSummary = (
  orderSummary?: OrderSummary | null
): NormalizedOrderSummary | null => {
  return (
    orderSummary?.orderSummary?.reduce((acc, item) => {
      const { type, ...data } = item;
      const key = camelCase(type);
      return { ...acc, [key]: data };
    }, {} as NormalizedOrderSummary) || null
  );
};

export const getSubscriptionFromInvoice = async (
  invoice: Invoice,
  ctx?: GetServerSidePropsContext
) => {
  if (!invoice) {
    return null;
  }

  const invoiceSub = invoice?.items?.find((i) => i.type === "subscription");
  const subRes = await getSubscription({ id: invoiceSub?.object?.id }, ctx);
  const subscription = isAPIError(subRes) ? null : subRes.subscription;
  return subscription;
};

export const getOnboardingConfirmationReadingLevelImage = ({
  contentfulBoxImagesRaw,
  gsuResponse,
}: {
  contentfulBoxImagesRaw: any;
  gsuResponse: Awaited<PostCompleteGuidedQuizReturnType>;
}): Asset["fields"] | null => {
  const rawContentfulReadingLevelImages = contentfulBoxImagesRaw?.items?.[0];

  const readingLevelImagesByName = mapImageEntryReferencesByName(
    rawContentfulReadingLevelImages,
    getReadingLevelNameFromEntryName
  );

  const readingLevelName = gsuResponse?.subscription?.readingLevelName || "";

  const readingLevelImage = getReadingLevelImage(
    readingLevelImagesByName,
    readingLevelName
  );

  return readingLevelImage || null;
};

export type ServerSideRedirectOptionsMembershipFlow = {
  ctx: GetServerSidePropsContext;
} & Pick<MembershipState, "checkoutSession" | "invoice" | "isAuthenticated">;

export const getServerSideRedirectOptionsMembershipFlow = ({
  checkoutSession,
  ctx,
  invoice,
  isAuthenticated,
}: ServerSideRedirectOptionsMembershipFlow) => {
  const { slug: s, ...queries } = ctx.query;
  const slug = s as MembershipSlug;

  let shouldRedirect = false;
  let dest = "";

  if (!slug || slug === "onboarding-confirmation") {
    return { shouldRedirect };
  }

  const dummyUser = isDummyUser({ checkoutSession, isAuthenticated });
  const hasInvoice = Boolean(invoice?.id);

  const queryParamString = new URLSearchParams(
    queries as Record<string, string>
  ).toString();
  const query = Boolean(queryParamString) ? `?${queryParamString}` : "";

  const redirectToQuizGetStarted = !ctx.query.quizId;

  const redirectToGetStarted =
    slug !== "get-started" &&
    slug !== "select-books" &&
    !dummyUser &&
    !isAuthenticated;

  const redirectToSelectBooks =
    slug !== "select-books" &&
    (dummyUser || Boolean(isAuthenticated)) &&
    !hasInvoice;

  const redirectToShipping =
    slug !== "shipping" && Boolean(isAuthenticated) && hasInvoice;

  shouldRedirect =
    redirectToQuizGetStarted ||
    redirectToGetStarted ||
    redirectToSelectBooks ||
    redirectToShipping;

  if (redirectToQuizGetStarted) {
    dest = `/quiz/get-started`;
  } else if (redirectToGetStarted) {
    dest = `/membership/get-started/`;
  } else if (redirectToSelectBooks) {
    dest = `/membership/select-books/`;
  } else if (redirectToShipping) {
    dest = `/membership/shipping/`;
  }

  const destination = `${dest}${query}`;

  if (shouldRedirect) {
    return {
      shouldRedirect,
      redirect: {
        destination,
        permanent: false,
      },
    };
  }

  return {
    shouldRedirect: false,
  };
};

export const isDummyUser = ({
  checkoutSession,
  isAuthenticated,
}: Pick<MembershipState, "checkoutSession" | "isAuthenticated">) => {
  return Boolean(checkoutSession?.accountInfo) && Boolean(!isAuthenticated);
};

export type GuidedSignUpAnswers = Record<string, OnboardingQuizAnswerSchema[]>;

export type ServerSidePropsMembership = {
  guidedSignUpAnswers: GuidedSignUpAnswers | null;
  orderSummary: OrderSummary | null;
} & Pick<
  MembershipState,
  | "checkoutSession"
  | "childAge"
  | "budgetValues"
  | "contentfulBoxImagesRaw"
  | "contentfulQuizModelParsed"
  | "contentfulQuizModelRaw"
  | "genderValues"
  | "goalsValues"
  | "invoice"
  | "isAuthenticated"
  | "isDummyUser"
  | "plansPrepaid"
  | "plans"
  | "quizId"
  | "readingLevel"
  | "readingLevelBadgesNoText"
  | "readingLevelBoxImage"
  | "readingLevelValues"
  | "recommendations"
  | "relationshipValues"
  | "selectPlanImageProps"
  | "selectedPlan"
  | "shippingWindow"
  | "shippingValues"
  | "subscription"
  | "themePreferenceValues"
>;

export const isPrepaidPlan = (slug: string) => {
  return (
    slug === "prepaid-3-month-0224" ||
    slug === "prepaid-6-month-0224" ||
    slug === "prepaid-12-month-0224" ||
    slug === "prepaid-monthly"
  );
};

/**
 * @note - initially for prepaid plan test
 * @see https://app.shortcut.com/literati/epic/64853/personalized-pre-paid-plan-2024
 */
export const getPrepaidPlansSorted = (plans: SubscriptionPlanCamelCase[]) => {
  const prepaidPlans = plans.filter((p) => isPrepaidPlan(p.slug));

  if (!prepaidPlans.length) {
    return null;
  }

  const plansSortedByBoxCount = prepaidPlans.sort((a, b) => {
    if (!a?.boxCount || !b?.boxCount) {
      return 0;
    }

    if (a.boxCount < b.boxCount) {
      return -1;
    } else if (a.boxCount > a.boxCount) {
      return 1;
    } else {
      return 0;
    }
  });

  const firstPlan = plansSortedByBoxCount.shift();

  return [...plansSortedByBoxCount, firstPlan] as PlanPrepaid[];
};

export const getServerSidePropsMembership = async (
  ctx: GetServerSidePropsContext
): Promise<ServerSidePropsMembership> => {
  const { slug: _, ...queries } = ctx.query;
  const { id, quizId = "" } = queries;
  const qid = quizId as string;

  const [
    auth,
    checkoutSessionRes,
    contentfulBoxImagesRaw,
    rawContentfulReadingLevelBadgesNoTextReferences,
    contentfulQuizModelParsed,
    contentfulQuizModelRaw,
    orderDetails,
    orderSummary,
    prefetchedPlans,
    recommendationsRes,
    shippingWindowRes,
  ] = await Promise.all([
    getAuth(ctx),
    getCheckoutSession(ctx),
    getEntriesBySlug("manyReferences", "book-box-images-cropped", {
      raw: true,
    }),
    getEntriesBySlug("manyReferences", "reading-level-images-without-text", {
      raw: true,
    }),
    getEntriesBySlug("guidedSignUp", "guided-sign-up-funnel"),
    getEntriesBySlug("guidedSignUp", "guided-sign-up-funnel", { raw: true }),
    getOrderDetails({ id }, ctx),
    getOrderSummary(ctx),
    getSubscriptionPlans(ctx),
    getBookShelfQuizRecommendations({ quizId: qid, ctx }),
    getShippingWindow(),
  ]);

  const isAuthenticated = isAPIError(auth)
    ? null
    : Boolean(auth.isAuthenticated);
  const checkoutSession = isAPIError(checkoutSessionRes)
    ? null
    : checkoutSessionRes;
  const dummyUser = isDummyUser({ checkoutSession, isAuthenticated });

  const completedGSUQuiz =
    dummyUser || isAuthenticated
      ? await completeGSUQuiz({
          ctx,
          contentfulBoxImagesRaw,
          contentfulQuizModelParsed,
        })
      : null;

  const { birthMonth, birthYear, firstName } =
    (checkoutSession?.personalization as ChildInfo) || {};
  const childInfo =
    birthMonth && birthYear && firstName
      ? await updateChildInfo?.({
          birthMonth,
          birthYear,
          firstName,
        })
      : null;

  const isValidInvoice = !isAPIError(orderDetails) && orderDetails?.invoice;
  const invoice = isValidInvoice ? orderDetails.invoice : null;
  const subscription = await getSubscriptionFromInvoice(invoice, ctx);

  const { TBYB_ANNUAL_INC_20_CREDITS_60, TBYB_MONTHLY, TBYB_MONTHLY_2025 } =
    SUBSCRIPTION_PLAN_SLUGS;

  const plans: Plans | null = isAPIError(prefetchedPlans)
    ? null
    : {
        annual: prefetchedPlans.find(
          (p) => p.slug === TBYB_ANNUAL_INC_20_CREDITS_60
        ),
        monthly: prefetchedPlans.find((p) => p.slug === TBYB_MONTHLY),
        monthly2025: prefetchedPlans.find((p) => p.slug === TBYB_MONTHLY_2025),
      };

  const plansPrepaid = isAPIError(prefetchedPlans)
    ? null
    : getPrepaidPlansSorted(prefetchedPlans);

  const selectedPlan = isAPIError(checkoutSessionRes)
    ? null
    : getSelectedPlan({
        plans,
        plansPrepaid,
        selectedPlanSlug: checkoutSessionRes?.selectedPlan,
      }) || null;

  const selectPlanImageProps = await getPlanBoxImageProps({
    contentfulBoxImagesRaw,
    contentfulQuizModelRaw,
    ctx,
  });

  const guidedSignUpAnswers = await getGuidedSignUpAnswers({
    ctx,
    quizSlug: contentfulQuizModelParsed.slug,
    quizVersion: contentfulQuizModelParsed.version,
    subscriptionId: subscription?.id,
  });

  const rawContentfulReadingLevelBadgesNoText = isAPIError(
    rawContentfulReadingLevelBadgesNoTextReferences
  )
    ? null
    : rawContentfulReadingLevelBadgesNoTextReferences?.items?.[0];
  const readingLevelBadgesNoText = rawContentfulReadingLevelBadgesNoText
    ? mapImageEntryReferencesByName(
        rawContentfulReadingLevelBadgesNoText,
        getReadingLevelNameFromEntryName
      )
    : null;

  const {
    budgetValues,
    genderValues,
    goalsValues,
    readingLevelValues,
    relationshipValues,
    themePreferenceValues,
  } = getInitialValuesGSU({
    contentfulQuizModelParsed,
    guidedSignUpAnswers: isAPIError(guidedSignUpAnswers)
      ? null
      : guidedSignUpAnswers,
    subscription,
  });

  const shippingValues = subscription?.shippingAddress
    ? { ...subscription.shippingAddress }
    : { ...checkoutSession?.shippingAddress };

  return {
    childAge: childInfo?.age ?? null,
    budgetValues,
    checkoutSession,
    contentfulBoxImagesRaw,
    contentfulQuizModelParsed: shuffleThemes(contentfulQuizModelParsed),
    contentfulQuizModelRaw,
    genderValues,
    goalsValues,
    guidedSignUpAnswers: isAPIError(guidedSignUpAnswers)
      ? null
      : guidedSignUpAnswers,
    invoice,
    isAuthenticated,
    isDummyUser: dummyUser,
    orderSummary: isAPIError(orderSummary) ? null : orderSummary,
    plans,
    plansPrepaid,
    quizId: qid || null,
    readingLevel: completedGSUQuiz?.readingLevel || null,
    readingLevelBadgesNoText,
    readingLevelBoxImage: completedGSUQuiz?.readingLevelBoxImage || null,
    readingLevelValues,
    recommendations: isAPIError(recommendationsRes)
      ? null
      : recommendationsRes.recommendations,
    relationshipValues,
    selectPlanImageProps,
    selectedPlan,
    shippingWindow: isAPIError(shippingWindowRes) ? null : shippingWindowRes,
    shippingValues: { kind: "shipping", ...shippingValues },
    subscription,
    themePreferenceValues,
  };
};

export interface GetInitialValuesGSU {
  contentfulQuizModelParsed?: any;
  guidedSignUpAnswers?: GuidedSignUpAnswers | null;
  subscription?: Subscription | null;
}

export const getInitialValuesRelationship = ({
  guidedSignUpAnswers,
}: GetInitialValuesGSU) => {
  const relationshipValues: RelationshipValuesPartial = {
    [RelationshipKey.KIND]: "relationship",
    [RelationshipKey.RELATIONSHIP]:
      (guidedSignUpAnswers?.["reader-relationship"]?.[0]?.value as string) ||
      "",
  };
  return relationshipValues;
};

export const getInitialValuesGoals = ({
  guidedSignUpAnswers,
}: GetInitialValuesGSU) => {
  const goalAnswers = guidedSignUpAnswers?.["reader-membership-goals"];
  const rawValues =
    goalAnswers
      ?.map((a) => a.value)
      .filter((v): v is string => typeof v === "string" && v.length > 0) ?? [];

  const selections: string[] = [];
  let otherGoalText = "";

  for (const v of rawValues) {
    if (MEMBERSHIP_GOAL_PRESET_VALUES.has(v)) {
      if (!selections.includes(v)) {
        selections.push(v);
      }
    } else {
      if (!otherGoalText) {
        otherGoalText = v;
      }
      if (!selections.includes("other")) {
        selections.push("other");
      }
    }
  }

  const goalsValues: GoalsValuesPartial = {
    [GoalsKey.KIND]: "goals",
    [GoalsKey.SELECTIONS]: selections.length > 0 ? selections : [],
    [GoalsKey.OTHER_GOAL_TEXT]: otherGoalText,
  };
  return goalsValues;
};

const MEMBERSHIP_GOAL_VALUE_TO_PILL = new Map(
  MEMBERSHIP_GOAL_OPTIONS.map((o) => [o.value, o.pillLabel] as const)
);

function membershipGoalLabelsFromGoalsPartial(
  goalsValues: GoalsValuesPartial | null | undefined
): string[] {
  const selections = goalsValues?.[GoalsKey.SELECTIONS];
  if (!selections?.length) {
    return [];
  }
  const otherText = (goalsValues?.[GoalsKey.OTHER_GOAL_TEXT] ?? "").trim();
  // StepGoals submit merges free-text into `selections` (`[...base, trimmedOther]`).
  // Only preset slugs are real choices; ignore appended custom text so "other" isn't shown twice.
  const seenPreset = new Set<string>();
  const presetSlugs = selections.filter((slug): slug is string => {
    if (typeof slug !== "string" || !MEMBERSHIP_GOAL_PRESET_VALUES.has(slug)) {
      return false;
    }
    if (seenPreset.has(slug)) {
      return false;
    }
    seenPreset.add(slug);
    return true;
  });

  const labels: string[] = [];
  for (const slug of presetSlugs) {
    if (slug === "other") {
      labels.push(
        otherText || MEMBERSHIP_GOAL_VALUE_TO_PILL.get("other") || "Other"
      );
    } else {
      const pill = MEMBERSHIP_GOAL_VALUE_TO_PILL.get(slug);
      labels.push(pill ?? slug);
    }
  }
  return labels;
}

function membershipGoalLabelsFromReaderAnswers(
  goalAnswers: OnboardingQuizAnswerSchema[] | null | undefined
): string[] {
  if (!goalAnswers?.length) {
    return [];
  }
  const partial = getInitialValuesGoals({
    guidedSignUpAnswers: { "reader-membership-goals": goalAnswers },
  });
  return membershipGoalLabelsFromGoalsPartial(partial);
}

/**
 * Human-readable membership goal lines (e.g. appended to reading level age pills on the card).
 */
export function getMembershipGoalLabelsForReadingCard({
  goalsValues,
  readerMembershipGoalAnswers,
}: {
  goalsValues?: GoalsValuesPartial | null;
  readerMembershipGoalAnswers?: OnboardingQuizAnswerSchema[] | null;
}): string[] {
  if (goalsValues?.[GoalsKey.SELECTIONS]?.length) {
    return membershipGoalLabelsFromGoalsPartial(goalsValues);
  }
  return membershipGoalLabelsFromReaderAnswers(readerMembershipGoalAnswers);
}

export const getInitialValuesReadingLevel = ({
  subscription,
}: GetInitialValuesGSU) => {
  const { readingLevelGrade, estimatedGrade } = subscription || {};
  let estimatedGradeNumber;
  if (readingLevelGrade && !isNaN(readingLevelGrade)) {
    estimatedGradeNumber = Math.floor(readingLevelGrade);
  } else {
    if (estimatedGrade && !isNaN(estimatedGrade)) {
      estimatedGradeNumber = Math.floor(estimatedGrade);
    }
  }
  const estimatedGradeFormValue =
    typeof estimatedGradeNumber === "number"
      ? estimatedGradeNumber < -3
        ? "-3"
        : estimatedGradeNumber?.toString()
      : null;

  const readingLevelValues: ReadingLevelValuesPartial = {
    [ReadingLevelKey.KIND]: "reading-level",
    [ReadingLevelKey.GRADE_ADJUSTMENT_KEY]: estimatedGradeFormValue || "",
    [ReadingLevelKey.GRADE_ADJUSTMENT]:
      subscription?.readingLevelAdjustment?.toString() || "",
  };

  return readingLevelValues;
};

export const getInitialValuesGender = ({
  guidedSignUpAnswers,
}: GetInitialValuesGSU) => {
  const genderValues: GenderValuesPartial = {
    [GenderKey.KIND]: "gender",
    [GenderKey.GENDER]:
      (guidedSignUpAnswers?.["reader-gender"]?.[0]?.value as string) || "",
  };
  return genderValues;
};

export const getInitialValuesThemePreferences = ({
  contentfulQuizModelParsed,
  subscription,
}: GetInitialValuesGSU) => {
  const step = contentfulQuizModelParsed?.quizSteps?.find(
    (step: ContentfulStep) => step.slug === "reader-theme-preference"
  );
  const options = step?.questions?.[0]?.answers;
  const optionsWithAnswers = options?.map((option: any) => {
    const camelCaseSlug = camelCase(option?.slug);
    return {
      ...option,
      value: subscription?.themePreference?.[camelCaseSlug] || 0,
    };
  });

  const themePreferenceValues: ThemePreferenceValuesPartial = {
    [ThemePreferenceKey.KIND]: "reader-theme-preferences",
    [ThemePreferenceKey.THEME_PREFERENCES]: optionsWithAnswers || undefined,
  };

  return themePreferenceValues;
};

export const getInitialValuesBudget = ({
  guidedSignUpAnswers,
}: GetInitialValuesGSU) => {
  const budgetValues: BudgetValuesPartial = {
    [BudgetKey.KIND]: "budget",
    [BudgetKey.BUDGET]:
      (guidedSignUpAnswers?.["reader-budget"]?.[0]?.value as string) || "",
  };
  return budgetValues;
};

export const getInitialValuesGSU = ({
  contentfulQuizModelParsed,
  guidedSignUpAnswers,
  subscription,
}: GetInitialValuesGSU) => {
  return {
    budgetValues: getInitialValuesBudget({ guidedSignUpAnswers }),
    genderValues: getInitialValuesGender({ guidedSignUpAnswers }),
    goalsValues: getInitialValuesGoals({ guidedSignUpAnswers }),
    readingLevelValues: getInitialValuesReadingLevel({ subscription }),
    relationshipValues: getInitialValuesRelationship({ guidedSignUpAnswers }),
    themePreferenceValues: getInitialValuesThemePreferences({
      contentfulQuizModelParsed,
      subscription,
    }),
  };
};

/**
 * Posts quiz completion and returns derived reading level information
 */
export const completeGSUQuiz = async ({
  contentfulBoxImagesRaw,
  contentfulQuizModelParsed,
  ctx,
}: {
  // apologies for the any types - we can be more specific when we migrate off the contentful models
  contentfulBoxImagesRaw: any;
  contentfulQuizModelParsed: any;
  ctx?: GetServerSidePropsContext;
}) => {
  const gsuResponse = await postCompleteGuidedQuiz({
    ctx,
    quizSlug: contentfulQuizModelParsed?.slug,
    quizVersion: contentfulQuizModelParsed?.version,
  });

  if (isAPIError(gsuResponse)) {
    return {
      readingLevel: null,
      readingLevelBoxImage: null,
    };
  }

  const readingLevelName =
    gsuResponse.subscription.readingLevelName?.toLowerCase();
  const readingLevel = contentfulQuizModelParsed.readingSkills.json.find(
    (s: ReadingSkillJsonItem) => s.name?.toLowerCase() === readingLevelName
  );

  const readingLevelBoxImage = getOnboardingConfirmationReadingLevelImage({
    contentfulBoxImagesRaw,
    gsuResponse,
  });

  return {
    readingLevel,
    readingLevelBoxImage,
  };
};

export const shouldSkipReadingLevelStep = ({
  childAge,
}: Pick<MembershipState, "childAge">) => {
  return childAge !== undefined && childAge !== null && childAge < 4.5;
};

export const shouldSkipBookShuffle = ({
  childAge,
}: Pick<MembershipState, "childAge">) => {
  return typeof childAge !== "number";
};
